import {
  Box,
  Stack,
  Text,
  Switch,
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  FormControl,
  FormLabel,
  FormErrorMessage,
} from '@chakra-ui/core';
import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import { useStoreActions, useStoreState } from '../../../models/hooks';
import TextInput from './formInputs/TextInput';
import { TextWithTranslationsT } from './types';
import theme from '../../../styles/customTheme';

const firstNameTag = '<span data-tag="first_name">[first_name]</span> <span data-tag="last_name">[last_name]</span>';
const currentDateTag = '<span data-tag="current_datetime">[current_datetime]</span>';
export const initialDisclaimerText: TextWithTranslationsT = {
  en: `<p>I ${firstNameTag}, confirm that all information entered in the application from on ${currentDateTag} is accurate, and I understand that providing false information may result in my disqualification from the recruitment process or dismissal.</p>`,
  fr: `<p>Avis de non-responsabilité Je, ${firstNameTag}, confirme que toutes les informations saisies dans le formulaire d'embauche le ${currentDateTag} sont exactes, et je comprends que la fourniture de fausses informations peut entraîner ma disqualification du processus de recrutement ou de mon congédiement.</p>`,
};

const FinalSignature = React.memo(
  function FinalSignatureComponent() {
    const { i18n, t } = useTranslation('hrFormTemplate');

    const {
      currentLanguage,
      defaultLanguage,
      availableLanguages,
      needFinalSignature,
      disclaimerText,
      errorsByTarget,
    } = useStoreState((state) => state.formBuilder);

    const { setDisclaimerText, setNeedFinalSignature } = useStoreActions((actions) => actions.formBuilder);
    const [isEditable, setIsEditable] = useState(false);

    const [isAccordionExpanded, setIsAccordionExpanded] = useState(false);
    const error = errorsByTarget('signatureDetails');

    useEffect(() => {
      moment.locale(i18n.language.split('-')[0]);
    }, [i18n.language]);

    const handleSwitchChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        setNeedFinalSignature(e.target.checked);
      },
      [setNeedFinalSignature],
    );

    const handleAccordionChange = useCallback(() => {
      setIsAccordionExpanded((prev) => !prev);
    }, []);

    const value = React.useMemo(() => {
      return disclaimerText?.[currentLanguage] || initialDisclaimerText[currentLanguage];
    }, [currentLanguage, disclaimerText]);

    React.useMemo(
      () => {
        if (needFinalSignature) {
          const newDisclaimerText: TextWithTranslationsT = {};
          /* eslint-disable-next-line */
          for (const lang of availableLanguages) {
            if (lang !== defaultLanguage && !disclaimerText?.[lang]?.trim() && disclaimerText?.[defaultLanguage]?.trim()) {
              newDisclaimerText[lang] = disclaimerText?.[defaultLanguage];
            } else {
              /* eslint-disable-next-line */
              if (disclaimerText?.[lang]?.trim()) {
                newDisclaimerText[lang] = disclaimerText[lang];
              } else if (initialDisclaimerText[lang]?.trim()) {
                newDisclaimerText[lang] = initialDisclaimerText[lang];
              }
            }
          }
          const areTextsEqual = JSON.stringify(disclaimerText) === JSON.stringify(newDisclaimerText);

          if (!areTextsEqual) {
            setDisclaimerText(newDisclaimerText);
          }
        }
      },
      /* eslint-disable-next-line */
      [needFinalSignature, availableLanguages, defaultLanguage, disclaimerText],
    );

    return (
      <Box px={4}>
        <Accordion allowToggle index={isAccordionExpanded ? 0 : -1} onChange={handleAccordionChange}>
          <AccordionItem
            borderWidth="1px"
            borderColor={theme.colors.gray[200]}
            borderRadius="md"
            _focusWithin={{ boxShadow: 'none', outline: 'none' }}
            isFocusable={false}
          >
            <AccordionButton
              _focus={{ boxShadow: 'none' }}
              py={3}
              px={4}
              borderBottomWidth={isAccordionExpanded ? '1px' : '0px'}
              borderColor={theme.colors.gray[200]}
            >
              <FormControl display="flex" alignItems="flex-start" as="div">
                <Switch
                  id="hr-form-final-review-signature"
                  size="sm"
                  colorScheme="blue"
                  isChecked={needFinalSignature}
                  mr={3}
                  onChange={handleSwitchChange}
                  aria-labelledby="final-signature-label"
                />
                <Stack spacing={0} flex="1">
                  <FormLabel
                    htmlFor="hr-form-final-review-signature"
                    id="final-signature-label"
                    mb="0"
                    fontSize="sm"
                    fontWeight="bold"
                    lineHeight="short"
                    cursor="pointer"
                  >
                    {t('formBuilder.languageReviewModal.finalReviewSignature.label', { lng: currentLanguage })}
                  </FormLabel>

                  <Text fontSize="xs" color="gray.600" textAlign="left">
                    {t('formBuilder.languageReviewModal.finalReviewSignature.description', { lng: currentLanguage })}
                  </Text>
                </Stack>
              </FormControl>
              <AccordionIcon />
            </AccordionButton>
            <AccordionPanel pb={4} px={4} fontSize="sm">
              <Text variant="h6" fontSize="sm" mb={2} fontWeight="bold">
                {t('formBuilder.languageReviewModal.finalReviewSignature.disclaimerTitle', { lng: currentLanguage })}
              </Text>
              {!isEditable ? (
                <>
                  <Text
                    dangerouslySetInnerHTML={{ __html: value }}
                    onClick={() => {
                      setIsEditable(true);
                    }}
                    cursor="text"
                    data-testid="final-signature-text"
                  />
                  <FormControl isInvalid={!!error}>
                    <FormErrorMessage mt={1} fontSize="xs">
                      {error}
                    </FormErrorMessage>
                  </FormControl>
                </>
              ) : (
                <TextInput
                  fieldId=""
                  property="signatureDetails"
                  innerPath={currentLanguage}
                  label={value}
                  placeholder="enter final review signature text"
                  maxLength={500}
                  onLabelChange={(e) => {
                    setNeedFinalSignature(true);
                    let currentValue = { ...disclaimerText };
                    /* eslint-disable-next-line */
                    for (const lang of availableLanguages) {
                      if (lang === currentLanguage) {
                        currentValue = { ...disclaimerText, [lang]: e };
                      } else {
                        /* eslint-disable-next-line */
                        if (!disclaimerText?.[lang]?.trim()) {
                          currentValue = { ...disclaimerText, [lang]: e };
                        }
                      }
                    }
                    setDisclaimerText({ ...currentValue });
                    setIsEditable(false);
                  }}
                  language={currentLanguage}
                  path={['signatureDetails', 'disclaimerText', currentLanguage]}
                  isAdvanced
                />
              )}
            </AccordionPanel>
          </AccordionItem>
        </Accordion>
      </Box>
    );
  },
  () => true,
);

export default FinalSignature;
